const MS_PER_DAY = 24 * 60 * 60 * 1000;

const FREQUENCY_INTERVAL_DAYS = Object.freeze({
  daily: 1,
  "every-other-day": 2,
  "twice-weekly": 3.5,
  weekly: 7,
  fortnightly: 14,
  "two-weekly": 14,
  biweekly: 14,
  monthly: 30,
  "one-off": null,
  once: null,
  "on-demand": null,
  adhoc: null,
});

const FREQUENCY_LOOKBACK_DAYS = Object.freeze({
  daily: 1,
  "every-other-day": 2,
  "twice-weekly": 7,
  weekly: 7,
  fortnightly: 14,
  "two-weekly": 14,
  biweekly: 14,
  monthly: 30,
  "one-off": null,
  once: null,
  "on-demand": null,
  adhoc: null,
});

export const PROTOCOL_TASK_STATES = Object.freeze({
  COMPLETED: "completed",
  DUE: "due",
  OVERDUE: "overdue",
  UNASSIGNED: "unassigned",
});

function normalizeFrequency(frequency) {
  const normalized =
    typeof frequency === "string" ? frequency.trim().toLowerCase() : "";
  return normalized || null;
}

function getFrequencyIntervalDays(frequency) {
  const normalized = normalizeFrequency(frequency);

  if (!normalized) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(FREQUENCY_INTERVAL_DAYS, normalized)) {
    return FREQUENCY_INTERVAL_DAYS[normalized];
  }

  return null;
}

function normalizeCount(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }

  return Math.floor(parsed);
}

function normalizeOccurrencesPerPeriod(value) {
  const parsed = Number(value);

  if (!Number.isInteger(parsed) || parsed < 1) {
    return 1;
  }

  return parsed;
}

export function isOneOffProtocolFrequency(frequency) {
  const normalized = normalizeFrequency(frequency);

  if (!normalized) {
    return false;
  }

  return (
    Object.prototype.hasOwnProperty.call(FREQUENCY_INTERVAL_DAYS, normalized) &&
    FREQUENCY_INTERVAL_DAYS[normalized] === null
  );
}

export function getProtocolTaskLookbackDays(frequency) {
  const normalized = normalizeFrequency(frequency);

  if (
    normalized &&
    Object.prototype.hasOwnProperty.call(FREQUENCY_LOOKBACK_DAYS, normalized)
  ) {
    return FREQUENCY_LOOKBACK_DAYS[normalized];
  }

  return null;
}

export function getExpectedOccurrencesForProtocolFrequency(
  frequency,
  occurrencesPerPeriod = 1,
) {
  const intervalDays = getFrequencyIntervalDays(frequency);
  const lookbackDays = getProtocolTaskLookbackDays(frequency);
  const normalizedOccurrencesPerPeriod =
    normalizeOccurrencesPerPeriod(occurrencesPerPeriod);

  if (intervalDays === null || lookbackDays === null) {
    return normalizedOccurrencesPerPeriod;
  }

  return (
    Math.max(1, Math.round(lookbackDays / intervalDays)) *
    normalizedOccurrencesPerPeriod
  );
}

export function resolveProtocolTaskState({
  expectedCount = 0,
  completedCount = 0,
  assigned = true,
  frequency = null,
  referenceTimeMs = null,
  nowMs = Date.now(),
}) {
  if (!assigned) {
    return PROTOCOL_TASK_STATES.UNASSIGNED;
  }

  if (normalizeCount(completedCount) >= normalizeCount(expectedCount)) {
    return PROTOCOL_TASK_STATES.COMPLETED;
  }

  const intervalDays = getFrequencyIntervalDays(frequency);
  const hasWindow =
    Number.isFinite(intervalDays) &&
    intervalDays > 0 &&
    Number.isFinite(referenceTimeMs) &&
    Number.isFinite(nowMs);

  if (hasWindow && nowMs - referenceTimeMs >= intervalDays * MS_PER_DAY) {
    return PROTOCOL_TASK_STATES.OVERDUE;
  }

  return PROTOCOL_TASK_STATES.DUE;
}

function resolveProtocolTaskDueAtMs({ frequency = null, referenceTimeMs = null }) {
  if (!Number.isFinite(referenceTimeMs)) {
    return null;
  }

  const intervalDays = getFrequencyIntervalDays(frequency);

  if (Number.isFinite(intervalDays) && intervalDays > 0) {
    return referenceTimeMs + intervalDays * MS_PER_DAY;
  }

  if (isOneOffProtocolFrequency(frequency)) {
    return referenceTimeMs;
  }

  return null;
}

function buildAssignedTask({
  kind,
  taskId,
  assignment,
  completedCount,
  order,
  referenceTimeMs,
  nowMs,
}) {
  const frequency = normalizeFrequency(assignment?.frequency);
  const occurrencesPerPeriod = normalizeOccurrencesPerPeriod(
    assignment?.occurrencesPerPeriod,
  );
  const expectedCount = getExpectedOccurrencesForProtocolFrequency(
    frequency,
    occurrencesPerPeriod,
  );

  return {
    kind,
    taskId,
    required: assignment?.required === true,
    frequency,
    occurrencesPerPeriod,
    expectedCount,
    completedCount: Math.min(normalizeCount(completedCount), expectedCount),
    dueAtMs: resolveProtocolTaskDueAtMs({
      frequency,
      referenceTimeMs,
    }),
    state: resolveProtocolTaskState({
      expectedCount,
      completedCount,
      assigned: true,
      frequency,
      referenceTimeMs,
      nowMs,
    }),
    order,
  };
}

function buildUnassignedTask(kind, taskId, order) {
  return {
    kind,
    taskId,
    required: false,
    frequency: null,
    expectedCount: 0,
    completedCount: 0,
    state: PROTOCOL_TASK_STATES.UNASSIGNED,
    order,
  };
}

function sortTasks(tasks) {
  const priority = {
    [PROTOCOL_TASK_STATES.OVERDUE]: 0,
    [PROTOCOL_TASK_STATES.DUE]: 1,
    [PROTOCOL_TASK_STATES.COMPLETED]: 2,
    [PROTOCOL_TASK_STATES.UNASSIGNED]: 3,
  };

  return [...tasks].sort((left, right) => {
    const priorityDelta =
      (priority[left.state] ?? Number.MAX_SAFE_INTEGER) -
      (priority[right.state] ?? Number.MAX_SAFE_INTEGER);

    if (priorityDelta !== 0) {
      return priorityDelta;
    }

    return left.order - right.order;
  });
}

export function buildProtocolTaskBoard({
  recordingAssignments = [],
  questionnaireAssignments = [],
  recordingCompletionByMode = {},
  questionnaireCompletionById = {},
  recordingLastCompletedAtByMode = {},
  questionnaireLastCompletedAtById = {},
  availableModeIds = [],
  availableQuestionnaireIds = [],
  protocolStartAtMs = null,
  nowMs = Date.now(),
}) {
  const assignedTasks = [
    ...recordingAssignments.map((assignment, index) =>
      buildAssignedTask({
        kind: "recording",
        taskId: assignment?.mode,
        assignment,
        completedCount: recordingCompletionByMode?.[assignment?.mode] ?? 0,
        order: index,
        referenceTimeMs:
          recordingLastCompletedAtByMode?.[assignment?.mode] ?? protocolStartAtMs,
        nowMs,
      }),
    ),
    ...questionnaireAssignments.map((assignment, index) =>
      buildAssignedTask({
        kind: "questionnaire",
        taskId: assignment?.questionnaireId,
        assignment,
        completedCount:
          questionnaireCompletionById?.[assignment?.questionnaireId] ?? 0,
        order: recordingAssignments.length + index,
        referenceTimeMs:
          questionnaireLastCompletedAtById?.[assignment?.questionnaireId] ??
          protocolStartAtMs,
        nowMs,
      }),
    ),
  ].filter((task) => task.taskId);

  const assignedModeIds = new Set(
    recordingAssignments
      .map((assignment) => assignment?.mode)
      .filter((value) => typeof value === "string" && value.length > 0),
  );
  const assignedQuestionnaireIds = new Set(
    questionnaireAssignments
      .map((assignment) => assignment?.questionnaireId)
      .filter((value) => typeof value === "string" && value.length > 0),
  );

  const unassignedTasks = [
    ...availableModeIds
      .filter((mode) => !assignedModeIds.has(mode))
      .map((mode, index) =>
        buildUnassignedTask("recording", mode, assignedTasks.length + index),
      ),
    ...availableQuestionnaireIds
      .filter((questionnaireId) => !assignedQuestionnaireIds.has(questionnaireId))
      .map((questionnaireId, index) =>
        buildUnassignedTask(
          "questionnaire",
          questionnaireId,
          assignedTasks.length + availableModeIds.length + index,
        ),
      ),
  ];

  if (assignedTasks.length === 0) {
    return {
      state:
        unassignedTasks.length > 0
          ? PROTOCOL_TASK_STATES.UNASSIGNED
          : "no-protocol",
      tasks: [],
      recordingTasks: [],
      questionnaireTasks: [],
      unassignedTasks,
      summary: {
        assignedTasksCount: 0,
        completedTasksCount: 0,
        dueTasksCount: 0,
        overdueTasksCount: 0,
        unassignedTasksCount: unassignedTasks.length,
      },
    };
  }

  const tasks = sortTasks(assignedTasks);
  const recordingTasks = tasks.filter((task) => task.kind === "recording");
  const questionnaireTasks = tasks.filter(
    (task) => task.kind === "questionnaire",
  );

  return {
    state: "ready",
    tasks,
    recordingTasks,
    questionnaireTasks,
    unassignedTasks,
    summary: {
      assignedTasksCount: tasks.length,
      completedTasksCount: tasks.filter(
        (task) => task.state === PROTOCOL_TASK_STATES.COMPLETED,
      ).length,
      dueTasksCount: tasks.filter(
        (task) => task.state === PROTOCOL_TASK_STATES.DUE,
      ).length,
      overdueTasksCount: tasks.filter(
        (task) => task.state === PROTOCOL_TASK_STATES.OVERDUE,
      ).length,
      unassignedTasksCount: unassignedTasks.length,
    },
  };
}
